import {User} from "../../../../api/accessControl/User.ts";
import {useEffect, useState} from "react";
import {BackendApi} from "../../../../api/api.ts";
import LoadingFrame from "../../../../components/LoadingFrame.tsx";
import Shell from "../../../../components/specific/Shell.tsx";
import {createTable, TableHeaderSpec} from "../../../../components/table/CreateTable.tsx";
import UserDetailsDrawer from "./UserDetailsDrawer.tsx";
import {mergeAccessRowsPerUser} from "./mergeAccessRowsPerUser.ts";

export default function UsersListPage({ currentUser }: { currentUser: User }) {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [users, setUsers] = useState<any[]>([])

    // pagination states for createTable
    const paginationState = useState<any>({ currentPage: 1, pageSize: 50 });
    const sortingState = useState<any>({ orderBy: 'id', orderDescending: false });

    // Drawer state
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

    async function onLoad() {
        const api = new BackendApi(currentUser.getToken());
        try {
            const response = await api.AccessControl__users({})
            const usersArray = Array.isArray(response) ? response : [];
            setUsers(mergeAccessRowsPerUser(usersArray));
        } catch (err) {
            console.error('Error loading users:', err);
            setUsers([]);
        } finally {
            setIsLoading(false)
        }
    }

    async function sendEmailVerificationEmail(user_id: string) {
        const api = new BackendApi(currentUser.getToken());
        api.AccessControl__user__sendEmailVerificationEmail({
            user_id: user_id
        }).then(() => alert('Success: Sent!')).catch(err => alert('Something went wrong. Please contact us via hello@pocketcfo.io | CODE: ERR52436 | err' + err))
    }

    useEffect(() => {
        onLoad()
    }, [])

    const tableHeaders: TableHeaderSpec<any> = {
        title: "",
        columns: [
            {
                name: 'Name',
                field: 'first_name',
                customRender: (row: any) => (
                    <>
                        <span className="text-sm leading-6 ">{row.first_name} {row.last_name}</span><br />
                        <span className="font-mono text-gray-500 text-xs">{row.id}</span>
                    </>
                )
            },
            {
                name: 'Email',
                field: 'email'
            },
            {
                name: 'Managing business',
                field: 'managing_business_names',
                customRender: (row: any) => (
                    <span className="text-sm">{row.managing_business_names?.join(', ') || '—'}</span>
                )
            },
            {
                name: 'Actions',
                field: 'actions',
                disableSorting: true,
                customRender: (row: any) => (
                    <div className="inline-flex rounded-md shadow-sm text-xs" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => sendEmailVerificationEmail(row.id)}
                            className="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                        >
                            Send email verification
                        </button>
                    </div>
                )
            }
        ],
        search: {
            searchFn: (row: any, searchState: any) => {
                if (!searchState.currentQuery) return true;
                const query = searchState.currentQuery.toLowerCase();
                return (
                    (row.first_name && row.first_name.toLowerCase().includes(query)) ||
                    (row.last_name && row.last_name.toLowerCase().includes(query)) ||
                    (row.email && row.email.toLowerCase().includes(query)) ||
                    (row.id && row.id.toLowerCase().includes(query))
                );
            }
        },
        pagination: {
            inMemory: true,
            paginationState: paginationState as any,
            defaultPageSize: 50,
            totalPages: Math.ceil(users.length / 50)
        },
        sorting: {
            inMemory: true,
            sortingState: sortingState as any
        },
        onRowClick: (row: any) => {
            setSelectedUserId(row.id);
            setIsDrawerOpen(true);
        },
        options: {
            allowFullRowClick: true
        }
    }

    const TableComponent = createTable<any>(users, tableHeaders);

    return (
        <div>
            <Shell currentUser={currentUser} isLoading={isLoading}>
                <h1 className="weight-600 text-3xl mb-6 ">Users</h1>
                <LoadingFrame isLoading={isLoading}>
                    {TableComponent()}
                </LoadingFrame>
            </Shell>

            <UserDetailsDrawer
                open={isDrawerOpen}
                setOpen={setIsDrawerOpen}
                userId={selectedUserId}
                currentUser={currentUser}
            />
        </div>
    )
}

